import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Share2, Heart, Camera, Star, ShieldCheck, Users, BedDouble, DoorOpen,
  MapPin, Navigation, MessageCircle, Mail, Phone, Sparkles, ChevronRight,
  Wifi, Car, UtensilsCrossed, Wind, Tv, Coffee, Waves, Trees, PawPrint, Bath, BatteryCharging,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PropertyMap } from "@/components/stays/PropertyMap";
import { AmenityChips } from "@/components/stays/AmenityChips";
import { fmtAmount } from "@/lib/stayflowApi";
import { isWishlisted, toggleWishlist } from "@/lib/wishlist";

const GoogleG = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 6.68 9.14 4.75 12 4.75Z" />
  </svg>
);

// Highlight metadata matched against real amenity strings (keyword → icon + blurb).
const HIGHLIGHTS = [
  { kw: ["wifi", "internet"], icon: Wifi, blurb: "Stay connected — stream and work with ease" },
  { kw: ["breakfast", "meal", "food"], icon: Coffee, blurb: "Authentic home-cooked local flavours" },
  { kw: ["kitchen"], icon: UtensilsCrossed, blurb: "Cook your own meals whenever you like" },
  { kw: ["parking"], icon: Car, blurb: "Hassle-free parking right at the stay" },
  { kw: ["ac", "air condition"], icon: Wind, blurb: "Cool comfort through warm afternoons" },
  { kw: ["power", "backup", "generator", "inverter"], icon: BatteryCharging, blurb: "Uninterrupted power, day and night" },
  { kw: ["garden", "lawn", "nature"], icon: Trees, blurb: "Green open space to unwind in" },
  { kw: ["hot water", "geyser"], icon: Bath, blurb: "Hot showers whenever you need one" },
  { kw: ["tv", "television"], icon: Tv, blurb: "Kick back with your favourite shows" },
  { kw: ["pool", "swimming"], icon: Waves, blurb: "Take a refreshing dip on-site" },
  { kw: ["pet"], icon: PawPrint, blurb: "Your furry friends are welcome too" },
];

const pickHighlights = (amenities = []) => {
  const out = [];
  for (const a of amenities) {
    const low = (a || "").toLowerCase();
    const meta = HIGHLIGHTS.find((h) => h.kw.some((k) => low.includes(k)));
    if (meta && !out.some((o) => o.meta === meta)) out.push({ title: a, meta });
    if (out.length === 3) break;
  }
  return out;
};

const Stars = ({ value = 0 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(value) ? "fill-[#F59E0B] text-[#F59E0B]" : "fill-black/10 text-black/10"}`} aria-hidden="true" />
    ))}
  </span>
);

const SectionTitle = ({ children }) => (
  <h2 className="font-heading font-extrabold text-xl tracking-tight text-ink">{children}</h2>
);

// App-style mobile property page: hero carousel, sticky quick-nav, card sections,
// sticky reserve bar. Desktop keeps the classic two-column layout.
const PropertyDetailMobile = ({ p, rooms = [], geo, rev, reviewsLoading, onShare, qs, propertyId, sp }) => {
  const navigate = useNavigate();

  const images = useMemo(() => {
    const roomPics = rooms.flatMap((r) => [r.cover_image, ...((r.photos || []).map((ph) => ph.url))]);
    return [...new Set([p.cover_image, ...roomPics].filter(Boolean))];
  }, [p.cover_image, rooms]);

  const [idx, setIdx] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const heroRef = useRef(null);
  const onHeroScroll = () => {
    const el = heroRef.current;
    if (el) setIdx(Math.min(images.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
  };

  const [saved, setSaved] = useState(() => isWishlisted(propertyId));
  useEffect(() => {
    const on = () => setSaved(isWishlisted(propertyId));
    window.addEventListener("sf-wishlist", on);
    return () => window.removeEventListener("sf-wishlist", on);
  }, [propertyId]);

  // derived real specs
  const capacity = rooms.reduce((s, r) => s + (Number(r.capacity) || 0), 0);
  const beds = rooms.reduce((s, r) => s + (Number(r.beds) || 0), 0);
  const highlights = useMemo(() => pickHighlights(p.amenities), [p.amenities]);
  const overline = [(p.type || p.property_type || "Homestay"), geo?.city || p.city].filter(Boolean).join(" · ");

  const price = p.min_price_incl_gst;

  const phoneDigits = (p.phone || "").replace(/\D/g, "");
  const waNumber = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits;
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi! I have a question about ${p.name} on StayFlow.`)}` : null;

  // ---- quick-nav ----
  const sections = useMemo(() => [
    { id: "overview", label: "Overview" },
    rooms.length > 0 && { id: "rooms", label: "Rooms" },
    (p.amenities || []).length > 0 && { id: "amenities", label: "Amenities" },
    geo?.lat != null && { id: "location", label: "Location" },
    { id: "reviews", label: "Reviews" },
    { id: "host", label: "Host" },
  ].filter(Boolean), [rooms.length, p.amenities, geo]);

  const [activeSec, setActiveSec] = useState("overview");
  const secRefs = useRef({});
  const pillRefs = useRef({});

  // Scroll-spy: nearest section above the fold; clamp to last section at page end
  // (short pages can't bring trailing sections into an observer band).
  useEffect(() => {
    const onScroll = () => {
      if (Date.now() < lockRef.current) return;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 8;
      if (atBottom) { setActiveSec(sections[sections.length - 1].id); return; }
      let cur = sections[0].id;
      for (const s of sections) {
        const el = secRefs.current[s.id];
        if (el && el.getBoundingClientRect().top <= 140) cur = s.id;
      }
      setActiveSec(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [sections]);

  useEffect(() => {
    pillRefs.current[activeSec]?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
  }, [activeSec]);

  const lockRef = useRef(0);
  const jump = useCallback((id) => {
    const el = secRefs.current[id];
    if (!el) return;
    lockRef.current = Date.now() + 1200; // keep the tapped pill active during the scroll
    setActiveSec(id);
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
    else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 64, behavior: "smooth" });
  }, []);

  const secProps = (id) => ({ ref: (el) => { secRefs.current[id] = el; }, "data-sec": id, className: "scroll-mt-16" });

  return (
    <div className="pb-28" data-testid="mdp-page">
      {/* ---- Edge-to-edge hero carousel ---- */}
      <div className="relative aspect-[4/3] overflow-hidden bg-paper-muted" data-testid="mdp-hero">
        <div ref={heroRef} onScroll={onHeroScroll} className="flex h-full overflow-x-auto snap-x snap-mandatory no-scrollbar">
          {images.map((src) => (
            <img key={src} src={src} alt={p.name} className="w-full h-full object-cover shrink-0 snap-center" />
          ))}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent pointer-events-none" />

        <div className="absolute top-3 inset-x-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="mdp-back"
            className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onShare} aria-label="Share this stay" data-testid="mdp-share"
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform">
              <Share2 className="w-[18px] h-[18px]" />
            </button>
            <motion.button whileTap={{ scale: 1.3 }} onClick={() => setSaved(toggleWishlist(propertyId))}
              aria-label={saved ? "Remove from wishlist" : "Save to wishlist"} data-testid="mdp-heart"
              className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center">
              <Heart className={`w-[19px] h-[19px] transition-colors ${saved ? "fill-brand text-brand" : "text-white"}`} />
            </motion.button>
          </div>
        </div>

        {images.length > 0 && (
          <button onClick={() => setGalleryOpen(true)} data-testid="mdp-photo-counter"
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/45 backdrop-blur-md text-white text-xs font-semibold px-3 py-1.5 active:scale-95 transition-transform">
            <Camera className="w-3.5 h-3.5" /> {idx + 1} / {images.length} Photos
          </button>
        )}
      </div>

      {/* ---- Sticky quick-nav ---- */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-black/5" data-testid="mdp-quicknav">
        <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2.5">
          {sections.map((s) => (
            <button key={s.id} ref={(el) => { pillRefs.current[s.id] = el; }} onClick={() => jump(s.id)} data-testid={`mdp-nav-${s.id}`}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors active:scale-95 ${
                activeSec === s.id ? "bg-ink text-white" : "bg-paper-muted text-ink-soft"
              }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 space-y-8 pt-5">
        {/* ---- Overview ---- */}
        <section {...secProps("overview")} data-testid="mdp-overview">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-faint">{overline}</p>
          <h1 className="mt-1.5 font-heading font-extrabold text-2xl leading-tight text-ink">{p.name}</h1>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="mdp-spec-pills">
            {capacity > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3 py-1.5 text-xs font-semibold text-ink"><Users className="w-3.5 h-3.5 text-brand" /> Up to {capacity} guests</span>}
            {rooms.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3 py-1.5 text-xs font-semibold text-ink"><DoorOpen className="w-3.5 h-3.5 text-brand" /> {rooms.length} room{rooms.length !== 1 ? "s" : ""}</span>}
            {beds > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-muted px-3 py-1.5 text-xs font-semibold text-ink"><BedDouble className="w-3.5 h-3.5 text-brand" /> {beds} bed{beds !== 1 ? "s" : ""}</span>}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-black/5 bg-white p-3.5 shadow-sm">
            {rev?.rating > 0 ? (
              <span className="inline-flex items-center gap-1.5 font-heading font-bold text-ink" data-testid="mdp-rating">
                <Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /> {Number(rev.rating).toFixed(2)}
                <span className="font-sans font-normal text-sm text-ink-soft">({rev.total} review{rev.total !== 1 ? "s" : ""})</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-heading font-bold text-ink" data-testid="mdp-rating">
                <Star className="w-4 h-4 fill-ink text-ink" /> New
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5" data-testid="mdp-verified">
              <ShieldCheck className="w-3.5 h-3.5" /> StayFlow Verified
            </span>
          </div>

          {p.description && <p className="mt-4 text-sm text-ink-soft leading-relaxed whitespace-pre-line">{p.description}</p>}

          {highlights.length > 0 && (
            <div className="mt-5 grid grid-cols-1 gap-2.5" data-testid="mdp-highlights">
              {highlights.map(({ title, meta }) => {
                const Icon = meta.icon;
                return (
                  <div key={title} className="flex items-center gap-3.5 rounded-2xl border border-black/5 bg-white p-3.5 shadow-sm">
                    <span className="w-11 h-11 shrink-0 rounded-xl bg-brand-soft text-brand flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                    <span>
                      <span className="block font-heading font-bold text-sm text-ink capitalize">{title}</span>
                      <span className="block text-xs text-ink-soft">{meta.blurb}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ---- Rooms slider ---- */}
        {rooms.length > 0 && (
          <section {...secProps("rooms")} data-testid="mdp-rooms">
            <div className="flex items-center justify-between">
              <SectionTitle>Where you'll sleep</SectionTitle>
              <span className="text-xs text-ink-faint">{rooms.length} room{rooms.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="mt-3 -mx-4 px-4 flex gap-3 overflow-x-auto snap-x no-scrollbar py-1">
              {rooms.map((r) => {
                const cover = r.cover_image || (r.photos || [])[0]?.url;
                return (
                  <Link key={r.id} to={`/stays/${propertyId}/${r.id}${qs}`} data-testid={`mdp-room-${r.id}`}
                    className="w-[240px] shrink-0 snap-start rounded-2xl bg-white border border-black/5 shadow-sm p-3 active:scale-[0.98] transition-transform">
                    <div className="aspect-video rounded-xl overflow-hidden bg-paper-muted">
                      {cover && <img src={cover} alt={r.name} loading="lazy" className="w-full h-full object-cover" />}
                    </div>
                    <p className="mt-2.5 font-heading font-bold text-sm text-ink line-clamp-1">{r.name}</p>
                    <p className="mt-0.5 text-xs text-ink-soft line-clamp-1">
                      {[r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : null, r.capacity ? `up to ${r.capacity} guests` : null, r.beds ? `${r.beds} bed${r.beds > 1 ? "s" : ""}` : null].filter(Boolean).join(" · ") || "Cozy private room"}
                    </p>
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-ink"><span className="font-heading font-extrabold">{fmtAmount(r.price_per_night_incl_gst)}</span><span className="text-xs text-ink-soft"> / night incl. GST</span></p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand">Book <ChevronRight className="w-3.5 h-3.5" /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* ---- Amenities ---- */}
        {(p.amenities || []).length > 0 && (
          <section {...secProps("amenities")} data-testid="mdp-amenities">
            <SectionTitle>What this place offers</SectionTitle>
            <div className="mt-3"><AmenityChips amenities={p.amenities.slice(0, 6)} testid="mdp-amenity-chips" /></div>
            {p.amenities.length > 6 && (
              <AmenitiesModal amenities={p.amenities} />
            )}
          </section>
        )}

        {/* ---- Location ---- */}
        {geo?.lat != null && (
          <section {...secProps("location")} data-testid="mdp-location">
            <div className="flex items-center justify-between mb-1">
              <SectionTitle>Where you'll be</SectionTitle>
              {geo.location_url && (
                <a href={geo.location_url} target="_blank" rel="noreferrer" data-testid="mdp-open-gmaps" className="text-xs font-semibold text-brand">Open in Google Maps →</a>
              )}
            </div>
            {geo.address && <p className="text-sm text-ink-soft mb-3 flex items-start gap-1.5"><MapPin className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {geo.address}</p>}
            <PropertyMap lat={geo.lat} lng={geo.lng} name={p.name} height={192} />
          </section>
        )}

        {/* ---- Reviews ---- */}
        <section {...secProps("reviews")} data-testid="mdp-reviews">
          <div className="flex items-center justify-between">
            <SectionTitle>
              {rev?.rating > 0 ? `★ ${Number(rev.rating).toFixed(2)} · ${rev.total} reviews` : "Guest reviews"}
            </SectionTitle>
            {rev?.rating > 0 && (
              <a href={rev.place_url || "#"} target={rev.place_url ? "_blank" : undefined} rel="noreferrer" data-testid="mdp-google-attribution"
                className="inline-flex items-center gap-1 text-[11px] text-ink-faint"><GoogleG className="w-3.5 h-3.5" /> from Google</a>
            )}
          </div>
          {reviewsLoading && <div className="mt-3 h-36 rounded-2xl bg-paper-muted animate-pulse" />}
          {!reviewsLoading && (rev?.reviews || []).length === 0 && (
            <div className="mt-3 rounded-2xl border border-black/5 bg-white p-6 text-center shadow-sm" data-testid="mdp-reviews-empty">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-soft text-brand-dark px-3.5 py-1.5 text-sm font-semibold">
                <Sparkles className="w-4 h-4" /> New
              </span>
              <p className="mt-2.5 text-sm text-ink-soft">This homestay just joined StayFlow. Be one of the first guests to review it.</p>
            </div>
          )}
          {!reviewsLoading && (rev?.reviews || []).length > 0 && (
            <div className="mt-3 -mx-4 px-4 flex gap-3 overflow-x-auto snap-x no-scrollbar py-1">
              {rev.reviews.map((r, i) => (
                <div key={`${r.author_name}-${i}`} className="w-[280px] shrink-0 snap-start rounded-2xl border border-black/5 bg-white p-4 shadow-sm" data-testid="mdp-review-card">
                  <div className="flex items-center gap-2.5">
                    {r.profile_photo_url ? (
                      <img src={r.profile_photo_url} alt={r.author_name} referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center text-xs font-bold">
                        {(r.author_name || "G").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-heading font-semibold text-sm text-ink truncate">{r.author_name || "Google user"}</p>
                      <p className="text-[11px] text-ink-faint">{r.relative_time_description}</p>
                    </div>
                  </div>
                  <div className="mt-2"><Stars value={r.rating} /></div>
                  {r.text && <p className="mt-1.5 text-[13px] text-ink-soft leading-relaxed line-clamp-3">{r.text}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ---- Host ---- */}
        <section {...secProps("host")} data-testid="mdp-host">
          <SectionTitle>Meet your host</SectionTitle>
          <div className="mt-3 rounded-2xl border border-black/5 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="relative shrink-0">
                <div className="w-14 h-14 rounded-full bg-ink text-white flex items-center justify-center font-heading font-extrabold text-xl">
                  {(p.name || "S").slice(0, 1).toUpperCase()}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-heading font-bold text-ink truncate">{p.name}</p>
                <p className="text-xs text-ink-soft">Verified StayFlow partner · Local host</p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {waHref && (
                <a href={waHref} target="_blank" rel="noreferrer" data-testid="mdp-ask-host"
                  className="inline-flex items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold px-5 py-2.5 active:scale-95 transition-transform">
                  <MessageCircle className="w-4 h-4" /> Ask host a question
                </a>
              )}
              {p.phone && (
                <a href={`tel:${p.phone}`} data-testid="mdp-call-host"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white text-sm font-semibold text-ink px-5 py-2.5 active:scale-95 transition-transform">
                  <Phone className="w-4 h-4 text-brand" /> Call
                </a>
              )}
              {!p.phone && p.email && (
                <a href={`mailto:${p.email}?subject=${encodeURIComponent(`Question about ${p.name} on StayFlow`)}`} data-testid="mdp-email-host"
                  className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white text-sm font-semibold text-ink px-5 py-2.5 active:scale-95 transition-transform">
                  <Mail className="w-4 h-4 text-brand" /> Email host
                </a>
              )}
              {!p.phone && !p.email && <p className="text-sm text-ink-faint">Contact details are shared after booking.</p>}
            </div>
          </div>
        </section>
      </div>

      {/* ---- Sticky reserve bar ---- */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-t border-black/10 px-5 py-3 pb-[max(env(safe-area-inset-bottom),12px)] flex items-center justify-between gap-4" data-testid="mdp-reserve-bar">
        <div className="min-w-0">
          <p className="font-heading font-extrabold text-lg text-ink leading-tight">
            {fmtAmount(price) || "View rooms"} {price != null && <span className="font-sans font-normal text-sm text-ink-soft">/ night</span>}
          </p>
          <p className="text-[11px] text-ink-soft truncate">{price != null ? "incl. GST · " : ""}Pick a room for the exact total</p>
        </div>
        <button onClick={() => jump("rooms")} data-testid="mdp-reserve-btn"
          className="shrink-0 rounded-2xl bg-brand text-white font-heading font-bold px-7 py-3 shadow-[0_10px_28px_rgba(255,26,26,0.4)] active:scale-95 transition-transform">
          Reserve Now
        </button>
      </div>

      {/* ---- Full-screen gallery ---- */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-full w-screen h-[100dvh] p-0 gap-0 rounded-none bg-ink border-none overflow-hidden" data-testid="mdp-gallery">
          <DialogTitle className="sr-only">All photos</DialogTitle>
          <div className="h-full overflow-y-auto p-3 pt-14 space-y-3" data-lenis-prevent>
            {images.map((src, i) => (
              <img key={src} src={src} alt={`${p.name} photo ${i + 1}`} className="w-full rounded-2xl" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AmenitiesModal = ({ amenities }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="mdp-amenities-all"
        className="mt-3 w-full rounded-2xl border border-black/15 bg-white py-3 text-sm font-semibold text-ink active:scale-[0.98] transition-transform">
        Show all {amenities.length} amenities
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="mdp-amenities-modal">
          <DialogTitle className="font-heading font-extrabold text-xl text-ink">All amenities</DialogTitle>
          <div className="max-h-[60vh] overflow-y-auto" data-lenis-prevent>
            <AmenityChips amenities={amenities} testid="mdp-amenities-full" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyDetailMobile;
